import {
  ALL_LAYOUT_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  Metadata,
  POSITIONING_ARGS,
  pick,
  pickAll,
} from '../../../utils/spds/metadata';
import { BUTTON_SIZES, NORMAL } from '../../../utils/spds/constants/sizes';
import { CENTER } from '../../../utils/spds/constants/css-layout';
import { HTML_BUTTON_TYPES, TYPE_BUTTON } from '../../../utils/spds/constants/buttons';
import { ROLE_BUTTON } from '../../../utils/spds/constants/aria';
import { tracked } from '@glimmer/tracking';
import Container from './container.gjs';
import SpdsAutofocus from '../../../modifiers/spds-autofocus';
import Super from './base.gjs';
import styles from './button.module.scss';

const METADATA = Metadata.define({
  description: `
    Base button component for SPDS with flexible layout options and styling

    ### Customization

    The HTML tag for the component can be customized by providing the \`@tag\` argument. In the case that this is set to something other
    than \`button\`, the component will automatically apply the HTML attribute \`role\` with the value \`button\` to the generated HTML
    element.

    This button supports all of our standard button CSS classes (primary, secondary, ghost, etc).

    Wraps the SPDS Core Container component.
  `,
  extends: [
    pick(COMPONENT_STATE_ARGS, [
      'autofocus',
      'disabled',
      { name: 'error', description: 'Apply error styling to the button' },
    ]),
    pick(CONTAINER_ARGS, [
      'column',
      'fitContent',
      'fitContentHeight',
      'fitContentWidth',
      'grid',
      'reverse',
      { name: 'inline', description: 'Display button inline' },
      { name: 'tag', default: TYPE_BUTTON },
    ]),
    pick(CONTENT_ARGS, ['iconOnly', { name: 'text', initialValue: 'Button' }]),
    pick(POSITIONING_ARGS, ['absolute', 'fixed']),
    pickAll(ALL_LAYOUT_ARGS, {
      overrides: {
        alignItems: { default: CENTER },
        gap: { default: '8px' },
        justifyContent: { default: CENTER },
      },
    }),
  ],
  meta: {
    size: Arg.string({ default: NORMAL, description: 'Button size', options: BUTTON_SIZES }),
    type: Arg.string({
      default: TYPE_BUTTON,
      description: 'Button type attribute',
      options: HTML_BUTTON_TYPES,
    }),
  },
  name: 'Core Button',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=19170-11848&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/v/0/p/56566c-buttons',
  },
});

export default class SpdsCoreButton extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-button';
  styles = styles;

  get classesToMap() {
    return ['hasBorder', 'iconOnly', 'size'];
  }

  @tracked baseElement;

  get alignItems() {
    return this.argOrDefault('alignItems');
  }

  get isButtonTag() {
    return this.tag === this.constructor.metadata.getDefault('tag');
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent');
  }

  /**
   * determines if role attribute should be set
   * @return {string|undefined} button role attribute value when not a button element
   */
  get role() {
    return this.isButtonTag ? undefined : ROLE_BUTTON;
  }

  get tag() {
    return this.argOrDefault('tag');
  }

  // if we're not using a button tag, we don't need a type attribute
  get type() {
    return this.isButtonTag ? this.argOrDefault('type') : undefined;
  }

  /**
   * determines the variant style of the button for data-test attributes
   * @return {string} button variant value
   */
  get variant() {
    if (this.link) return 'link';
    if (this.variantClass) return this.variantClass.split('-').at(-1);

    return this.tag;
  }

  <template>
    <Container
      @absolute={{@absolute}}
      @alignContent={{@alignContent}}
      @alignItems={{this.alignItems}}
      @alignSelf={{@alignSelf}}
      @area={{@area}}
      @areas={{@areas}}
      @autoFlow={{@autoFlow}}
      @column={{@column}}
      @columns={{@columns}}
      @content={{@text}}
      @error={{@error}}
      @fitContent={{@fitContent}}
      @fitContentHeight={{@fitContentHeight}}
      @fitContentWidth={{@fitContentWidth}}
      @fixed={{@fixed}}
      @flex={{@flex}}
      @flexWrap={{@flexWrap}}
      @gap={{this.gap}}
      @grid={{@grid}}
      @gridArea={{@gridArea}}
      @gridAutoColumns={{@gridAutoColumns}}
      @gridAutoFlow={{@gridAutoFlow}}
      @gridAutoRows={{@gridAutoRows}}
      @gridColumn={{@gridColumn}}
      @gridRow={{@gridRow}}
      @gridTemplateAreas={{@gridTemplateAreas}}
      @gridTemplateColumns={{@gridTemplateColumns}}
      @gridTemplateRows={{@gridTemplateRows}}
      @inline={{@inline}}
      @justifyContent={{this.justifyContent}}
      @justifyItems={{@justifyItems}}
      @justifySelf={{@justifySelf}}
      @order={{@order}}
      @reverse={{@reverse}}
      @rows={{@rows}}
      @subgrid={{@subgrid}}
      @tag={{this.tag}}
      class={{this.classes}}
      data-test-spds-button={{this.variant}}
      disabled={{@disabled}}
      role={{this.role}}
      type={{this.type}}
      ...attributes
      {{SpdsAutofocus @autofocus}}
      {{this.setupComponent}}
      {{this.validateArguments}}
    >
      {{yield}}
    </Container>
  </template>
}

export const CORE_BUTTON_METADATA = SpdsCoreButton.metadata;
